import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { appTodayDate } from '../lib/appDateTime';
import { 
  FileText, 
  Download, 
  Calendar,
  TrendingUp,
  TrendingDown,
  Bird,
  PawPrint,
  Turtle,
  Building2,
  Heart,
  Skull,
  ArrowRightLeft,
  Timer,
  RefreshCw,
  BarChart3,
  PieChart,
  FileSpreadsheet,
  Table,
  FileDown,
  Loader2
} from 'lucide-react';
import { SHOW_INSTITUTO_BW_BRANDING } from '../lib/branding';

const MONTHS = [
  { value: '1', label: 'Janeiro' },
  { value: '2', label: 'Fevereiro' },
  { value: '3', label: 'Março' },
  { value: '4', label: 'Abril' },
  { value: '5', label: 'Maio' },
  { value: '6', label: 'Junho' },
  { value: '7', label: 'Julho' },
  { value: '8', label: 'Agosto' },
  { value: '9', label: 'Setembro' },
  { value: '10', label: 'Outubro' },
  { value: '11', label: 'Novembro' },
  { value: '12', label: 'Dezembro' }
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - i);

export default function ReportsPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [report, setReport] = useState(null);
  const [annualReport, setAnnualReport] = useState(null);
  const [bases, setBases] = useState([]);
  const [animals, setAnimals] = useState([]);
  
  // Filters
  const [selectedMonth, setSelectedMonth] = useState(String(new Date().getMonth() + 1));
  const [selectedYear, setSelectedYear] = useState(String(currentYear));
  const [selectedBase, setSelectedBase] = useState('all');
  const [originCityFilter, setOriginCityFilter] = useState('');
  const [originStateFilter, setOriginStateFilter] = useState('');
  const [originRegionFilter, setOriginRegionFilter] = useState('');
  const [activeTab, setActiveTab] = useState('monthly');
  
  // Export state
  const [exportType, setExportType] = useState('admissions');
  const [exportStatus, setExportStatus] = useState('all');
  const [exportFormat, setExportFormat] = useState('csv');
  const [animalExportId, setAnimalExportId] = useState('');
  const [exportingData, setExportingData] = useState(false);
  const [ibamaPeriodType, setIbamaPeriodType] = useState('month');
  const [ibamaStartDate, setIbamaStartDate] = useState(`${currentYear}-01-01`);
  const [ibamaEndDate, setIbamaEndDate] = useState(appTodayDate());

  useEffect(() => {
    fetchBases();
    fetchAnimals();
  }, []);

  useEffect(() => {
    if (activeTab === 'monthly') {
      fetchMonthlyReport();
    } else {
      fetchAnnualReport();
    }
  }, [selectedMonth, selectedYear, selectedBase, originCityFilter, originStateFilter, originRegionFilter, activeTab]);

  const fetchBases = async () => {
    try {
      const response = await axios.get(`${API}/bases`);
      setBases(response.data);
    } catch (error) {
      console.error('Error fetching bases:', error);
    }
  };

  const fetchAnimals = async () => {
    try {
      const response = await axios.get(`${API}/admissions`);
      setAnimals(response.data || []);
    } catch (error) {
      console.error('Error fetching animals:', error);
    }
  };

  const fetchMonthlyReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('year', selectedYear);
      if (selectedMonth && selectedMonth !== 'all') {
        params.append('month', selectedMonth);
      }
      if (selectedBase && selectedBase !== 'all') {
        params.append('base_id', selectedBase);
      }
      if (originCityFilter.trim()) {
        params.append('origin_city', originCityFilter.trim());
      }
      if (originStateFilter.trim()) {
        params.append('origin_state', originStateFilter.trim());
      }
      if (originRegionFilter.trim()) {
        params.append('origin_region', originRegionFilter.trim());
      }
      
      const response = await axios.get(`${API}/reports/monthly?${params.toString()}`);
      setReport(response.data);
    } catch (error) {
      toast.error('Erro ao carregar relatório');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnnualReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('year', selectedYear);
      if (selectedBase && selectedBase !== 'all') {
        params.append('base_id', selectedBase);
      }
      if (originCityFilter.trim()) {
        params.append('origin_city', originCityFilter.trim());
      }
      if (originStateFilter.trim()) {
        params.append('origin_state', originStateFilter.trim());
      }
      if (originRegionFilter.trim()) {
        params.append('origin_region', originRegionFilter.trim());
      }
      
      const response = await axios.get(`${API}/reports/annual?${params.toString()}`);
      setAnnualReport(response.data);
    } catch (error) {
      toast.error('Erro ao carregar relatório anual');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const buildReportParams = ({ includeMonth = true } = {}) => {
    const params = new URLSearchParams();
    params.append('year', selectedYear);
    if (includeMonth && selectedMonth && selectedMonth !== 'all') {
      params.append('month', selectedMonth);
    }
    if (selectedBase && selectedBase !== 'all') {
      params.append('base_id', selectedBase);
    }
    if (originCityFilter.trim()) {
      params.append('origin_city', originCityFilter.trim());
    }
    if (originStateFilter.trim()) {
      params.append('origin_state', originStateFilter.trim());
    }
    if (originRegionFilter.trim()) {
      params.append('origin_region', originRegionFilter.trim());
    }
    return params;
  };

  const downloadBlob = (blobData, filename) => {
    const url = window.URL.createObjectURL(new Blob([blobData]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadReport = async (reportType, format = 'pdf') => {
    setDownloading(true);
    try {
      const includeMonth = reportType === 'monthly';
      const params = buildReportParams({ includeMonth });
      params.set('format', format);
      const endpoint = reportType === 'monthly' ? 'reports/monthly/export' : 'reports/annual/export';
      const response = await axios.get(`${API}/${endpoint}?${params.toString()}`, { responseType: 'blob' });
      const monthPart = selectedMonth && selectedMonth !== 'all' ? `_${selectedMonth.padStart(2, '0')}` : '';
      const ext = format === 'xls' || format === 'excel' ? 'xls' : format;
      const filename = reportType === 'monthly'
        ? `relatorio_mensal_${selectedYear}${monthPart}.${ext}`
        : `relatorio_anual_${selectedYear}.${ext}`;
      downloadBlob(response.data, filename);
      toast.success(`${format.toUpperCase()} baixado com sucesso!`);
    } catch (error) {
      toast.error('Erro ao gerar relatório');
      console.error(error);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      params.append('year', selectedYear);
      if (selectedMonth && selectedMonth !== 'all') {
        params.append('month', selectedMonth);
      }
      if (selectedBase && selectedBase !== 'all') {
        params.append('base_id', selectedBase);
      }
      if (originCityFilter.trim()) {
        params.append('origin_city', originCityFilter.trim());
      }
      if (originStateFilter.trim()) {
        params.append('origin_state', originStateFilter.trim());
      }
      if (originRegionFilter.trim()) {
        params.append('origin_region', originRegionFilter.trim());
      }
      
      const response = await axios.get(`${API}/reports/monthly/pdf?${params.toString()}`, {
        responseType: 'blob'
      });
      
      downloadBlob(response.data, `relatorio_mensal_${selectedYear}_${selectedMonth.padStart(2, '0')}.pdf`);
      
      toast.success('PDF baixado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar PDF');
      console.error(error);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadAnnualPDF = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      params.append('year', selectedYear);
      if (selectedBase && selectedBase !== 'all') params.append('base_id', selectedBase);
      if (originCityFilter.trim()) params.append('origin_city', originCityFilter.trim());
      if (originStateFilter.trim()) params.append('origin_state', originStateFilter.trim());
      if (originRegionFilter.trim()) params.append('origin_region', originRegionFilter.trim());

      const response = await axios.get(`${API}/reports/annual/pdf?${params.toString()}`, {
        responseType: 'blob'
      });

      downloadBlob(response.data, `relatorio_anual_${selectedYear}.pdf`);
      toast.success('PDF anual baixado com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar PDF anual');
    } finally {
      setDownloading(false);
    }
  };

  const handleExportData = async () => {
    setExportingData(true);
    try {
      const params = new URLSearchParams();
      params.append('year', selectedYear);
      if (selectedMonth && selectedMonth !== 'all') {
        params.append('month', selectedMonth);
      }
      if (selectedBase && selectedBase !== 'all') {
        params.append('base_id', selectedBase);
      }
      if (originCityFilter.trim()) {
        params.append('origin_city', originCityFilter.trim());
      }
      if (originStateFilter.trim()) {
        params.append('origin_state', originStateFilter.trim());
      }
      if (originRegionFilter.trim()) {
        params.append('origin_region', originRegionFilter.trim());
      }
      if (exportStatus && exportStatus !== 'all') {
        params.append('status', exportStatus);
      }

      if (exportType === 'ibama') {
        params.set('period_type', ibamaPeriodType);
        params.set('format', exportFormat);
        if (ibamaPeriodType === 'custom') {
          if (!ibamaStartDate || !ibamaEndDate) {
            toast.error('Informe a data inicial e final do relatório IBAMA');
            setExportingData(false);
            return;
          }
          params.set('start_date', ibamaStartDate);
          params.set('end_date', ibamaEndDate);
        } else {
          params.set('year', selectedYear);
          if (ibamaPeriodType === 'month') {
            params.set('month', selectedMonth === 'all' ? String(new Date().getMonth() + 1) : selectedMonth);
          }
        }
      }

      if (['animal-dossier', 'outcome-individual', 'necropsy-individual'].includes(exportType) && !animalExportId.trim()) {
        toast.error('Informe o ID do animal para este tipo de exportação');
        setExportingData(false);
        return;
      }

      let response;
      const monthFilenamePart = selectedMonth === 'all' ? 'all' : selectedMonth.padStart(2, '0');
      const normalizedFormat = exportFormat === 'excel' ? 'xls' : exportFormat;
      let filename = `${exportType}_${selectedYear}_${monthFilenamePart}.${normalizedFormat}`;

      if (exportType === 'monthly') {
        params.set('format', exportFormat);
        response = await axios.get(`${API}/reports/monthly/export?${params.toString()}`, { responseType: 'blob' });
        filename = `relatorio_mensal_${selectedYear}_${monthFilenamePart}.${normalizedFormat}`;
      } else if (exportType === 'annual') {
        params.delete('month');
        params.set('format', exportFormat);
        response = await axios.get(`${API}/reports/annual/export?${params.toString()}`, { responseType: 'blob' });
        filename = `relatorio_anual_${selectedYear}.${normalizedFormat}`;
      } else if (exportType === 'animal-dossier') {
        response = await axios.get(`${API}/animals/${animalExportId.trim()}/dossier/pdf`, { responseType: 'blob' });
        filename = `dossie_${animalExportId.trim()}.pdf`;
      } else if (exportType === 'ibama') {
        response = await axios.get(`${API}/reports/ibama/export?${params.toString()}`, { responseType: 'blob' });
        const periodFilename = ibamaPeriodType === 'custom'
          ? `${ibamaStartDate}_${ibamaEndDate}`
          : ibamaPeriodType === 'year'
            ? selectedYear
            : `${selectedYear}_${(selectedMonth === 'all' ? String(new Date().getMonth() + 1) : selectedMonth).padStart(2, '0')}`;
        filename = `relatorio_ibama_${periodFilename}.${exportFormat}`;
      } else if (exportType === 'period-dossier') {
        params.set('format', exportFormat);
        const endpoint = 'reports/period-dossier/export';
        response = await axios.get(`${API}/${endpoint}?${params.toString()}`, { responseType: 'blob' });
        filename = `dossie_periodo_${selectedYear}_${monthFilenamePart}.${normalizedFormat}`;
      } else if (exportType === 'outcome-individual') {
        response = await axios.get(`${API}/animals/${animalExportId.trim()}/outcome/pdf`, { responseType: 'blob' });
        filename = `desfecho_${animalExportId.trim()}.pdf`;
      } else if (exportType === 'necropsy-individual') {
        const listResponse = await axios.get(`${API}/animals/${animalExportId.trim()}/necropsies`);
        const necropsy = Array.isArray(listResponse.data) ? listResponse.data[0] : null;
        if (!necropsy?.id) {
          throw new Error('Necropsia não encontrada para o animal informado');
        }
        response = await axios.get(`${API}/necropsies/${necropsy.id}/pdf`, { responseType: 'blob' });
        filename = `necropsia_${animalExportId.trim()}.pdf`;
      } else {
        params.set('format', exportFormat);
        const endpoint = {
          'admissions': 'export/admissions',
          'daily-records': 'export/daily-records',
          'exams': 'export/exams'
        }[exportType];
        response = await axios.get(`${API}/${endpoint}?${params.toString()}`, {
          responseType: 'blob'
        });
        filename = `${exportType}_${selectedYear}_${monthFilenamePart}.${normalizedFormat}`;
      }

      downloadBlob(response.data, filename);
      
      toast.success('Dados exportados com sucesso!');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Erro ao exportar dados');
      console.error(error);
    } finally {
      setExportingData(false);
    }
  };

  const getGroupIcon = (group) => {
    switch (group) {
      case 'AVE': return <Bird className="w-4 h-4" />;
      case 'MAM': return <PawPrint className="w-4 h-4" />;
      case 'REPT': return <Turtle className="w-4 h-4" />;
      default: return null;
    }
  };

  const getGroupColor = (group) => {
    switch (group) {
      case 'AVE': return 'bg-sky-500 text-white';
      case 'MAM': return 'bg-amber-500 text-white';
      case 'REPT': return 'bg-green-600 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  // Simple bar chart component
  const SimpleBarChart = ({ data, maxValue, color = '#10b981' }) => {
    const max = maxValue || Math.max(...data.map(d => d.value), 1);
    return (
      <div className="flex items-end gap-1 h-32">
        {data.map((item, index) => (
          <div key={index} className="flex-1 flex flex-col items-center">
            <div 
              className="w-full rounded-t transition-all duration-500"
              style={{ 
                height: `${(item.value / max) * 100}%`,
                backgroundColor: color,
                minHeight: item.value > 0 ? '4px' : '0'
              }}
            />
            <span className="text-xs text-gray-500 mt-1">{item.label}</span>
            <span className="text-xs font-medium">{item.value}</span>
          </div>
        ))}
      </div>
    );
  };

  if (loading && !report && !annualReport) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          <p className="mt-4 text-emerald-700">Carregando relatório...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-emerald-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="hidden md:flex items-center justify-between gap-6">
            <div className="flex items-center space-x-3 min-w-0">
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img
                  src="/bw-symbol-final.png"
                  alt="Instituto BW"
                  className="h-12 w-12 object-contain"
                />
              )}
              <div className={`min-w-0 ${SHOW_INSTITUTO_BW_BRANDING ? 'border-l border-emerald-200 pl-3' : ''}`}>
                <h1 className="text-lg md:text-xl font-bold text-emerald-800 truncate" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Relatórios Gerenciais
                </h1>
                <p className="text-xs text-emerald-500 truncate">Estatísticas e Análises</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => activeTab === 'monthly' ? fetchMonthlyReport() : fetchAnnualReport()}
                variant="outline"
                size="sm"
                disabled={loading}
                className="border-emerald-600 text-emerald-600"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                size="sm"
                className="border-emerald-600 text-emerald-600"
              >
                Voltar
              </Button>
              <Button
                onClick={logout}
                variant="ghost"
                size="sm"
                className="text-red-600 hover:bg-red-50"
              >
                Sair
              </Button>
            </div>
          </div>

          <div className="md:hidden space-y-4">
            <div className="flex items-start space-x-3">
              {SHOW_INSTITUTO_BW_BRANDING && (
                <img
                  src="/bw-symbol-final.png"
                  alt="Instituto BW"
                  className="h-11 w-11 object-contain"
                />
              )}
              <div className={`min-w-0 ${SHOW_INSTITUTO_BW_BRANDING ? 'border-l border-emerald-200 pl-3' : ''}`}>
                <h1 className="text-lg font-bold text-emerald-800 break-words" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Relatórios Gerenciais
                </h1>
                <p className="text-xs text-emerald-500 break-words">Estatísticas e Análises</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Button
                onClick={() => activeTab === 'monthly' ? fetchMonthlyReport() : fetchAnnualReport()}
                variant="outline"
                size="sm"
                disabled={loading}
                className="w-full border-emerald-600 text-emerald-600"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                size="sm"
                className="w-full border-emerald-600 text-emerald-600"
              >
                Voltar
              </Button>
              <Button
                onClick={logout}
                variant="ghost"
                size="sm"
                className="w-full text-red-600 hover:bg-red-50"
              >
                Sair
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filters */}
        <Card className="mb-6 border-emerald-100">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-1">
                <Label className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4" /> Ano
                </Label>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger data-testid="year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {YEARS.map(year => (
                      <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {activeTab === 'monthly' && (
                <div className="flex-1">
                  <Label className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4" /> Mês
                  </Label>
                  <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                    <SelectTrigger data-testid="month-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map(month => (
                        <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex-1">
                <Label className="flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4" /> Base
                </Label>
                <Select value={selectedBase} onValueChange={setSelectedBase}>
                  <SelectTrigger data-testid="base-select">
                    <SelectValue placeholder="Todas as bases" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as bases</SelectItem>
                    {bases.map(base => (
                      <SelectItem key={base.id} value={base.id}>{base.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label className="flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4" /> Cidade de Origem
                </Label>
                <Input
                  value={originCityFilter}
                  onChange={(e) => setOriginCityFilter(e.target.value)}
                  placeholder="Ex: Santos"
                />
              </div>
              <div className="w-full md:w-40">
                <Label className="flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4" /> UF
                </Label>
                <Input
                  value={originStateFilter}
                  onChange={(e) => setOriginStateFilter(e.target.value.toUpperCase())}
                  placeholder="SP"
                  maxLength={2}
                />
              </div>
              <div className="flex-1">
                <Label className="flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4" /> Região
                </Label>
                <Input
                  value={originRegionFilter}
                  onChange={(e) => setOriginRegionFilter(e.target.value)}
                  placeholder="Ex: Litoral Norte"
                />
              </div>
              {activeTab === 'monthly' && (
                <div className="flex flex-wrap gap-2">
                  {['pdf', 'csv', 'xls'].map((format) => (
                    <Button
                      key={format}
                      onClick={() => handleDownloadReport('monthly', format)}
                      disabled={downloading || !report}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid={format === 'pdf' ? 'download-pdf-btn' : `download-monthly-${format}-btn`}
                    >
                      {downloading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                      {format.toUpperCase()}
                    </Button>
                  ))}
                </div>
              )}
              {activeTab === 'annual' && (
                <div className="flex flex-wrap gap-2">
                  {['pdf', 'csv', 'xls'].map((format) => (
                    <Button
                      key={format}
                      onClick={() => handleDownloadReport('annual', format)}
                      disabled={downloading || !annualReport}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {downloading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                      {format.toUpperCase()}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid w-full max-w-lg grid-cols-3">
            <TabsTrigger value="monthly" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Mensal
            </TabsTrigger>
            <TabsTrigger value="annual" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Anual
            </TabsTrigger>
            <TabsTrigger value="export" className="flex items-center gap-2" data-testid="export-tab">
              <FileSpreadsheet className="w-4 h-4" />
              Exportar
            </TabsTrigger>
          </TabsList>

          {/* Monthly Report */}
          <TabsContent value="monthly" className="mt-6">
            {report && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-emerald-100 text-sm">Admissões</p>
                          <p className="text-3xl font-bold">{report.summary.new_admissions}</p>
                        </div>
                        <TrendingUp className="w-8 h-8 text-emerald-200" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-sky-500 to-sky-600 text-white">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sky-100 text-sm">Solturas</p>
                          <p className="text-3xl font-bold">{report.summary.releases}</p>
                        </div>
                        <Heart className="w-8 h-8 text-sky-200" />
                      </div>
                      <p className="text-xs text-sky-100 mt-2">{report.summary.release_rate}% taxa</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-red-100 text-sm">Óbitos</p>
                          <p className="text-3xl font-bold">{report.summary.deaths}</p>
                        </div>
                        <Skull className="w-8 h-8 text-red-200" />
                      </div>
                      <p className="text-xs text-red-100 mt-2">{report.summary.mortality_rate}% taxa</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-purple-100 text-sm">Transferências</p>
                          <p className="text-3xl font-bold">{report.summary.transfers_out}</p>
                        </div>
                        <ArrowRightLeft className="w-8 h-8 text-purple-200" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-amber-100 text-sm">Ativos</p>
                          <p className="text-3xl font-bold">{report.summary.active_animals}</p>
                        </div>
                        <PawPrint className="w-8 h-8 text-amber-200" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Statistics by Group */}
                <Card className="border-emerald-100">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-emerald-800">
                      <PieChart className="w-5 h-5" />
                      Estatísticas por Grupo
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {['AVE', 'MAM', 'REPT'].map(group => (
                        <div key={group} className="border rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Badge className={getGroupColor(group)}>
                              {getGroupIcon(group)}
                              <span className="ml-1">
                                {group === 'AVE' ? 'Aves' : group === 'MAM' ? 'Mamíferos' : 'Répteis'}
                              </span>
                            </Badge>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Admissões:</span>
                              <span className="font-semibold">{report.by_group.admissions[group] || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Solturas:</span>
                              <span className="font-semibold text-emerald-600">{report.by_group.releases[group] || 0}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Óbitos:</span>
                              <span className="font-semibold text-red-600">{report.by_group.deaths[group] || 0}</span>
                            </div>
                            <div className="flex justify-between border-t pt-2">
                              <span className="text-gray-600">Ativos:</span>
                              <span className="font-bold text-emerald-700">{report.by_group.active[group] || 0}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Treatment Stats */}
                <Card className="border-emerald-100">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-emerald-800">
                      <Timer className="w-5 h-5" />
                      Tempo de Tratamento (Animais Soltos)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-emerald-50 rounded-lg">
                        <p className="text-sm text-gray-600">Tempo Médio</p>
                        <p className="text-3xl font-bold text-emerald-700">{report.treatment_stats.avg_days}</p>
                        <p className="text-xs text-gray-500">dias</p>
                      </div>
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm text-gray-600">Mínimo</p>
                        <p className="text-3xl font-bold text-blue-700">{report.treatment_stats.min_days}</p>
                        <p className="text-xs text-gray-500">dias</p>
                      </div>
                      <div className="text-center p-4 bg-orange-50 rounded-lg">
                        <p className="text-sm text-gray-600">Máximo</p>
                        <p className="text-3xl font-bold text-orange-700">{report.treatment_stats.max_days}</p>
                        <p className="text-xs text-gray-500">dias</p>
                      </div>
                      <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <p className="text-sm text-gray-600">Total Soltos</p>
                        <p className="text-3xl font-bold text-purple-700">{report.treatment_stats.total_released}</p>
                        <p className="text-xs text-gray-500">animais</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Admissions List */}
                {report.admissions_list.length > 0 && (
                  <Card className="border-emerald-100">
                    <CardHeader>
                      <CardTitle className="text-emerald-800">
                        Admissões do Mês ({report.admissions_list.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-emerald-50">
                            <tr>
                              <th className="text-left p-3 font-semibold text-emerald-800">ID IBAMA</th>
                              <th className="text-left p-3 font-semibold text-emerald-800">Nome Popular</th>
                              <th className="text-left p-3 font-semibold text-emerald-800">Espécie</th>
                              <th className="text-left p-3 font-semibold text-emerald-800">Grupo</th>
                              <th className="text-left p-3 font-semibold text-emerald-800">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {report.admissions_list.map((adm, idx) => (
                              <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="p-3 font-medium text-emerald-700">{adm.ibama_id}</td>
                                <td className="p-3">{adm.common_name}</td>
                                <td className="p-3 text-gray-600 italic">{adm.species}</td>
                                <td className="p-3">
                                  <Badge className={getGroupColor(adm.animal_group)} variant="secondary">
                                    {adm.animal_group}
                                  </Badge>
                                </td>
                                <td className="p-3">{adm.status}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          {/* Annual Report */}
          <TabsContent value="annual" className="mt-6">
            {annualReport && (
              <div className="space-y-6">
                {/* Annual Totals */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                    <CardContent className="p-6 text-center">
                      <p className="text-emerald-100">Total de Admissões</p>
                      <p className="text-5xl font-bold my-2">{annualReport.totals.admissions}</p>
                      <p className="text-sm text-emerald-100">Média: {annualReport.avg_monthly_admissions}/mês</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-sky-500 to-sky-600 text-white">
                    <CardContent className="p-6 text-center">
                      <p className="text-sky-100">Total de Solturas</p>
                      <p className="text-5xl font-bold my-2">{annualReport.totals.releases}</p>
                      <p className="text-sm text-sky-100">Média: {annualReport.avg_monthly_releases}/mês</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
                    <CardContent className="p-6 text-center">
                      <p className="text-red-100">Total de Óbitos</p>
                      <p className="text-5xl font-bold my-2">{annualReport.totals.deaths}</p>
                      <p className="text-sm text-red-100">
                        {annualReport.totals.admissions > 0 
                          ? `${((annualReport.totals.deaths / annualReport.totals.admissions) * 100).toFixed(1)}% do total`
                          : '0%'}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Monthly Chart */}
                <Card className="border-emerald-100">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-emerald-800">
                      <BarChart3 className="w-5 h-5" />
                      Admissões por Mês
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <SimpleBarChart 
                      data={annualReport.monthly_stats.map(m => ({
                        label: m.month_name?.substring(0, 3) || `M${m.month}`,
                        value: m.admissions
                      }))}
                      color="#10b981"
                    />
                  </CardContent>
                </Card>

                {/* Monthly Table */}
                <Card className="border-emerald-100">
                  <CardHeader>
                    <CardTitle className="text-emerald-800">Detalhamento Mensal</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-emerald-50">
                          <tr>
                            <th className="text-left p-3 font-semibold text-emerald-800">Mês</th>
                            <th className="text-center p-3 font-semibold text-emerald-800">Admissões</th>
                            <th className="text-center p-3 font-semibold text-emerald-800">Solturas</th>
                            <th className="text-center p-3 font-semibold text-emerald-800">Óbitos</th>
                            <th className="text-center p-3 font-semibold text-emerald-800">Ativos</th>
                          </tr>
                        </thead>
                        <tbody>
                          {annualReport.monthly_stats.map((month, idx) => (
                            <tr key={month.month} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="p-3 font-medium">{month.month_name || `Mês ${month.month}`}</td>
                              <td className="p-3 text-center">
                                <Badge variant="outline" className="bg-emerald-50">{month.admissions}</Badge>
                              </td>
                              <td className="p-3 text-center">
                                <Badge variant="outline" className="bg-sky-50 text-sky-700">{month.releases}</Badge>
                              </td>
                              <td className="p-3 text-center">
                                <Badge variant="outline" className="bg-red-50 text-red-700">{month.deaths}</Badge>
                              </td>
                              <td className="p-3 text-center font-semibold text-emerald-700">{month.active}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-emerald-100 font-bold">
                          <tr>
                            <td className="p-3">TOTAL</td>
                            <td className="p-3 text-center">{annualReport.totals.admissions}</td>
                            <td className="p-3 text-center">{annualReport.totals.releases}</td>
                            <td className="p-3 text-center">{annualReport.totals.deaths}</td>
                            <td className="p-3 text-center">-</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {annualReport.admissions_list?.length > 0 && (
                  <Card className="border-emerald-100">
                    <CardHeader>
                      <CardTitle className="text-emerald-800">Animais do Ano ({annualReport.admissions_list.length})</CardTitle>
                      <CardDescription>Lista detalhada dos animais do período filtrado.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-emerald-50">
                            <tr>
                              <th className="text-left p-3 font-semibold text-emerald-800">Número</th>
                              <th className="text-left p-3 font-semibold text-emerald-800">PMP</th>
                              <th className="text-left p-3 font-semibold text-emerald-800">Nome</th>
                              <th className="text-left p-3 font-semibold text-emerald-800">Espécie</th>
                              <th className="text-left p-3 font-semibold text-emerald-800">Base</th>
                              <th className="text-left p-3 font-semibold text-emerald-800">Origem</th>
                              <th className="text-left p-3 font-semibold text-emerald-800">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {annualReport.admissions_list.map((animal, idx) => (
                              <tr key={animal.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                <td className="p-3 font-medium text-emerald-700">{animal.ibama_id}</td>
                                <td className="p-3">{animal.pmp_id || 'N/A'}</td>
                                <td className="p-3">{animal.common_name}</td>
                                <td className="p-3 italic text-gray-600">{animal.species}</td>
                                <td className="p-3">{animal.base_name}</td>
                                <td className="p-3">{animal.origin_city || 'N/A'} / {animal.origin_state || 'N/A'}</td>
                                <td className="p-3">{animal.status}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          {/* Export Data Tab */}
          <TabsContent value="export" className="mt-6">
            <div className="space-y-6">
              {/* Export Card */}
              <Card className="border-emerald-100">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-emerald-800">
                    <FileSpreadsheet className="w-5 h-5" />
                    Exportar Dados e Relatórios
                  </CardTitle>
                  <CardDescription>
                    Gere arquivos do sistema para análise externa e prestação de contas
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Export Type Selection */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                      onClick={() => { setExportType('monthly'); setExportFormat('pdf'); }}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        exportType === 'monthly'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-emerald-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${exportType === 'monthly' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
                          <FileText className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold">Relatório Mensal</p>
                          <p className="text-sm text-gray-500">Resumo gerencial do mês</p>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => { setExportType('annual'); setExportFormat('pdf'); }}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        exportType === 'annual'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-emerald-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${exportType === 'annual' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
                          <BarChart3 className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold">Relatório Anual</p>
                          <p className="text-sm text-gray-500">Resumo e detalhamento do ano</p>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => setExportType('admissions')}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        exportType === 'admissions'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-emerald-300'
                      }`}
                      data-testid="export-admissions-option"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${exportType === 'admissions' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
                          <PawPrint className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold">Admissões</p>
                          <p className="text-sm text-gray-500">Dados completos de animais</p>
                        </div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => setExportType('daily-records')}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        exportType === 'daily-records'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-emerald-300'
                      }`}
                      data-testid="export-daily-option"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${exportType === 'daily-records' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
                          <Calendar className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold">Registros Diários</p>
                          <p className="text-sm text-gray-500">Acompanhamento veterinário</p>
                        </div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => setExportType('exams')}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        exportType === 'exams'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-emerald-300'
                      }`}
                      data-testid="export-exams-option"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${exportType === 'exams' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
                          <FileText className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold">Exames</p>
                          <p className="text-sm text-gray-500">Resultados laboratoriais</p>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setExportType('ibama');
                        setExportFormat('csv');
                        if (selectedMonth === 'all') setSelectedMonth(String(new Date().getMonth() + 1));
                      }}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        exportType === 'ibama'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-emerald-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${exportType === 'ibama' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
                          <FileSpreadsheet className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold">Relatório IBAMA</p>
                          <p className="text-sm text-gray-500">Modelo oficial em CSV, XLS ou PDF</p>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => { setExportType('animal-dossier'); setExportFormat('pdf'); }}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        exportType === 'animal-dossier'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-emerald-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${exportType === 'animal-dossier' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
                          <FileDown className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold">Dossiê por Animal</p>
                          <p className="text-sm text-gray-500">PDF completo com histórico e fotos</p>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => { setExportType('period-dossier'); setExportFormat('pdf'); }}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        exportType === 'period-dossier'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-emerald-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${exportType === 'period-dossier' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
                          <Table className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold">Dossiê por Período</p>
                          <p className="text-sm text-gray-500">PDF ou Excel compatível do período</p>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => { setExportType('outcome-individual'); setExportFormat('pdf'); }}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        exportType === 'outcome-individual'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-emerald-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${exportType === 'outcome-individual' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
                          <Heart className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold">Desfecho Individual</p>
                          <p className="text-sm text-gray-500">PDF do desfecho por animal</p>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => { setExportType('necropsy-individual'); setExportFormat('pdf'); }}
                      className={`p-4 rounded-lg border-2 transition-all text-left ${
                        exportType === 'necropsy-individual'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-emerald-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${exportType === 'necropsy-individual' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
                          <Skull className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-semibold">Necropsia Individual</p>
                          <p className="text-sm text-gray-500">PDF da necropsia por animal</p>
                        </div>
                      </div>
                    </button>
                  </div>

                  {/* Additional Filters */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t">
                    {exportType === 'ibama' && (
                      <div>
                        <Label className="flex items-center gap-2 mb-2">
                          <Calendar className="w-4 h-4" /> Período
                        </Label>
                        <Select value={ibamaPeriodType} onValueChange={setIbamaPeriodType}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="month">Mês</SelectItem>
                            <SelectItem value="year">Ano</SelectItem>
                            <SelectItem value="custom">Período específico</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {(exportType !== 'ibama' || ibamaPeriodType !== 'custom') && (
                      <div>
                        <Label className="flex items-center gap-2 mb-2">
                          <Calendar className="w-4 h-4" /> Ano
                        </Label>
                        <Select value={selectedYear} onValueChange={setSelectedYear}>
                          <SelectTrigger data-testid="export-year-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {YEARS.map(year => (
                              <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {(exportType !== 'ibama' || ibamaPeriodType === 'month') && (
                      <div>
                        <Label className="flex items-center gap-2 mb-2">
                          <Calendar className="w-4 h-4" /> Mês {exportType === 'ibama' ? '' : '(opcional)'}
                        </Label>
                        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                          <SelectTrigger data-testid="export-month-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {exportType !== 'ibama' && <SelectItem value="all">Todos os meses</SelectItem>}
                            {MONTHS.map(month => (
                              <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {exportType === 'ibama' && ibamaPeriodType === 'custom' && (
                      <>
                        <div>
                          <Label className="flex items-center gap-2 mb-2">
                            <Calendar className="w-4 h-4" /> Data inicial
                          </Label>
                          <Input type="date" value={ibamaStartDate} onChange={(e) => setIbamaStartDate(e.target.value)} />
                        </div>
                        <div>
                          <Label className="flex items-center gap-2 mb-2">
                            <Calendar className="w-4 h-4" /> Data final
                          </Label>
                          <Input type="date" value={ibamaEndDate} onChange={(e) => setIbamaEndDate(e.target.value)} />
                        </div>
                      </>
                    )}
                    {exportType === 'admissions' && (
                      <div>
                        <Label className="flex items-center gap-2 mb-2">
                          <Table className="w-4 h-4" /> Status
                        </Label>
                        <Select value={exportStatus} onValueChange={setExportStatus}>
                          <SelectTrigger data-testid="export-status-select">
                            <SelectValue placeholder="Todos" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            <SelectItem value="Internado">Internado</SelectItem>
                            <SelectItem value="Em tratamento">Em tratamento</SelectItem>
                            <SelectItem value="Alta">Alta</SelectItem>
                            <SelectItem value="Óbito">Óbito</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {['animal-dossier', 'outcome-individual', 'necropsy-individual'].includes(exportType) && (
                      <div>
                        <Label className="flex items-center gap-2 mb-2">
                          <PawPrint className="w-4 h-4" /> ID do Animal
                        </Label>
                        <Select value={animalExportId} onValueChange={setAnimalExportId}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione um animal" />
                          </SelectTrigger>
                          <SelectContent>
                            {animals.map((animal) => (
                              <SelectItem key={animal.id} value={animal.id}>
                                {animal.ibama_id} - {animal.common_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {['monthly', 'annual', 'admissions', 'daily-records', 'exams', 'period-dossier'].includes(exportType) && (
                      <div>
                        <Label className="flex items-center gap-2 mb-2">
                          <FileDown className="w-4 h-4" /> Formato
                        </Label>
                        <Select value={exportFormat} onValueChange={setExportFormat}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pdf">PDF</SelectItem>
                            <SelectItem value="csv">CSV</SelectItem>
                            <SelectItem value="excel">Excel compatível (.xls)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {exportType === 'ibama' && (
                      <div>
                        <Label className="flex items-center gap-2 mb-2">
                          <FileDown className="w-4 h-4" /> Formato
                        </Label>
                        <Select value={exportFormat} onValueChange={setExportFormat}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="csv">CSV</SelectItem>
                            <SelectItem value="xls">Excel compatível (.xls)</SelectItem>
                            <SelectItem value="pdf">PDF horizontal</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  {/* Export Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                      <FileDown className="w-4 h-4" />
                      Campos incluídos na exportação:
                    </h4>
                    <div className="text-sm text-blue-700">
                      {exportType === 'admissions' && (
                        <p>ID IBAMA, Nome Popular, Espécie, Grupo, Status, Data Admissão, Base, Local, Baia, Origem, Cidade, Motivo, Desfecho, Data Desfecho, Dias em Tratamento</p>
                      )}
                      {exportType === 'monthly' && (
                        <p>Admissões do mês com ID IBAMA, nome vulgar, nome científico, grupo, data de entrada, base e status, respeitando os filtros da tela.</p>
                      )}
                      {exportType === 'annual' && (
                        <p>Detalhamento anual dos animais com número, PMP, nomes, grupo, base, origem, data de entrada e status, respeitando os filtros da tela.</p>
                      )}
                      {exportType === 'daily-records' && (
                        <p>ID IBAMA, nome comum, nome científico, cidade, data de entrada, data do registro, apetite, comportamento, evolução clínica, prognóstico, dias em tratamento e responsável.</p>
                      )}
                      {exportType === 'exams' && (
                        <p>ID Exame, ID IBAMA, nome comum, nome científico, cidade, data de entrada, tipo de exame, categoria/nível, coleta, liberação do laudo, laboratório, realizado em, responsável, observações, parâmetros e anexos.</p>
                      )}
                      {exportType === 'ibama' && (
                        <p>Número de registro IBAMA, FAI/PMP, nome vulgar, nome científico, sexo, identificação, condições físicas de entrada, entrada, origem, marcações, status, saída, necropsia, causa da morte, desfecho, local de destino, autorizações e requerimentos disponíveis.</p>
                      )}
                      {exportType === 'animal-dossier' && (
                        <p>Dossiê completo do animal com admissão, prontuário, exames, alimentação, movimentações, desfecho, necropsia, dias em tratamento e imagens disponíveis.</p>
                      )}
                      {exportType === 'period-dossier' && (
                        <p>Dossiê consolidado do período selecionado, reunindo admissões e histórico completo dos animais do recorte.</p>
                      )}
                      {exportType === 'outcome-individual' && (
                        <p>PDF oficial do desfecho do animal informado.</p>
                      )}
                      {exportType === 'necropsy-individual' && (
                        <p>PDF individual da necropsia vinculada ao animal informado.</p>
                      )}
                    </div>
                  </div>

                  {/* Export Button */}
                  <div className="flex justify-end pt-4">
                    <Button
                      onClick={handleExportData}
                      disabled={exportingData}
                      className="bg-emerald-600 hover:bg-emerald-700 min-w-[200px]"
                      data-testid="export-data-btn"
                    >
                      {exportingData ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Exportando...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          {exportType === 'period-dossier'
                            ? `Exportar ${exportFormat === 'excel' ? 'XLS' : exportFormat.toUpperCase()}`
                            : exportType === 'ibama'
                              ? `Exportar ${exportFormat.toUpperCase()}`
                            : ['animal-dossier', 'outcome-individual', 'necropsy-individual'].includes(exportType)
                              ? 'Exportar PDF'
                              : `Exportar ${exportFormat === 'excel' ? 'XLS' : exportFormat.toUpperCase()}`}
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              {report && (
                <Card className="border-emerald-100">
                  <CardHeader>
                    <CardTitle className="text-emerald-800 text-lg">
                      Resumo dos Dados Disponíveis
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-3 bg-emerald-50 rounded-lg">
                        <p className="text-2xl font-bold text-emerald-700">{report.summary.active_animals}</p>
                        <p className="text-sm text-gray-600">Animais Ativos</p>
                      </div>
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <p className="text-2xl font-bold text-blue-700">{report.summary.new_admissions}</p>
                        <p className="text-sm text-gray-600">Admissões no Mês</p>
                      </div>
                      <div className="text-center p-3 bg-purple-50 rounded-lg">
                        <p className="text-2xl font-bold text-purple-700">{report.summary.releases}</p>
                        <p className="text-sm text-gray-600">Solturas no Mês</p>
                      </div>
                      <div className="text-center p-3 bg-amber-50 rounded-lg">
                        <p className="text-2xl font-bold text-amber-700">{report.treatment_stats.total_released}</p>
                        <p className="text-sm text-gray-600">Total Soltos</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
